import type { TaxonomyCampaign } from '@/entities/taxonomy-campaign';
import type { TaxonomyDataset } from '@/entities/taxonomy-dataset';

/**
 * The sentinel id for the Ungrouped bucket.
 *
 * A real campaign id is a uuid, so this cannot collide with one. It exists
 * because the bucket needs a key for React and for the expansion store, and
 * `null` is not usable as either.
 */
// eslint-disable-next-line i18next/no-literal-string -- a cache/React key, never rendered
export const UNGROUPED_ID = 'ungrouped';

export interface CampaignGroup {
  /** `null` for the Ungrouped bucket. */
  campaign: TaxonomyCampaign | null;
  id: string;
  /** Taxonomies to render under this group, already filtered by the search. */
  datasets: TaxonomyDataset[];
  /**
   * How many taxonomies the group holds IN TOTAL, ignoring the search.
   *
   * The badge counts the group, not the query. Counting the filtered list would
   * make "2 taxonomies" flicker down to "1 taxonomy" as someone types, which
   * reads as taxonomies being removed.
   */
  totalCount: number;
}

interface GroupOptions {
  campaigns: TaxonomyCampaign[];
  datasets: TaxonomyDataset[];
  /** Already trimmed and lowercased, or empty when not searching. */
  term: string;
  /** Renders a dataset's schema name, which the search also matches on. */
  schemaLabel: (dataset: TaxonomyDataset) => string;
}

/**
 * Builds the two-level tree the list renders.
 *
 * Campaigns come back alphabetically (the API already sorts, this does not
 * re-sort), each holding the taxonomies filed under it in the order the dataset
 * list supplied. Ungrouped is always LAST — it is a remainder, not a peer, and
 * putting it first would push real campaigns below a bucket that is often the
 * largest.
 *
 * An empty campaign still appears when not searching. It is a real row someone
 * created and can still delete or file something under; hiding it would make a
 * campaign whose last taxonomy was just deleted look like it vanished.
 *
 * Membership only. Whether a group renders open is `useExpandedCampaigns`'s
 * business, and splitting it that way is deliberate: expansion with two owners
 * is expansion no toggle can reconcile.
 */
export function groupByCampaign({
  campaigns,
  datasets,
  term,
  schemaLabel,
}: GroupOptions): CampaignGroup[] {
  const matchesDataset = (d: TaxonomyDataset) =>
    d.name.toLowerCase().includes(term) || schemaLabel(d).toLowerCase().includes(term);

  const known = new Set(campaigns.map((c) => c.id));

  const byCampaign = new Map<string, TaxonomyDataset[]>();
  for (const dataset of datasets) {
    /*
     * A `campaignId` naming a campaign this render does not have falls back to
     * Ungrouped rather than being dropped.
     *
     * Without the `known` check a taxonomy pointing at an unknown campaign
     * matches no group and is rendered by nothing — it simply vanishes from the
     * list. That is reachable in ordinary use: the two queries are independent,
     * so a campaign deleted in another tab, or a campaigns request that failed
     * while the dataset list came back from cache, leaves exactly this state.
     * Showing the taxonomy in the wrong group is recoverable; showing the user
     * an empty list where their work used to be is not.
     */
    const key =
      dataset.campaignId && known.has(dataset.campaignId) ? dataset.campaignId : UNGROUPED_ID;
    const bucket = byCampaign.get(key);
    if (bucket) {
      bucket.push(dataset);
    } else {
      byCampaign.set(key, [dataset]);
    }
  }

  const groups: CampaignGroup[] = [];

  const push = (campaign: TaxonomyCampaign | null, id: string) => {
    const all = byCampaign.get(id) ?? [];
    const campaignMatches = Boolean(term) && (campaign?.name.toLowerCase().includes(term) ?? false);

    if (!term) {
      groups.push({ campaign, id, datasets: all, totalCount: all.length });
      return;
    }

    /*
     * A campaign whose own name matches keeps ALL its taxonomies. Someone
     * searching for a campaign wants the campaign, not the subset of its
     * contents that happen to repeat its name.
     */
    if (campaignMatches) {
      groups.push({ campaign, id, datasets: all, totalCount: all.length });
      return;
    }

    const hits = all.filter(matchesDataset);
    if (hits.length > 0) {
      groups.push({ campaign, id, datasets: hits, totalCount: all.length });
    }
  };

  for (const campaign of campaigns) {
    push(campaign, campaign.id);
  }

  // Ungrouped last, and only when it holds something — unlike a campaign, it is
  // not a row anyone created, so an empty one is nothing to show.
  if ((byCampaign.get(UNGROUPED_ID) ?? []).length > 0) {
    push(null, UNGROUPED_ID);
  }

  return groups;
}
